/**
 * Gráfica de "Ventas por vendedor" por período (presentacional + su hook de datos).
 *
 * Réplica de la gráfica de vendedores del SSR (`advanced_stats_with_charts.html`,
 * `loadSellerChart`): el ranking de vendedores por ingreso en el período (día/
 * semana/mes/año), acotado a los 15 primeros (igual que el SSR), con el rango de
 * fechas del backend. Gestiona su período y su query (`useSalesBySeller`, cacheada
 * por sede + período). El `ChartCard` contempla carga/error.
 */
import { useState } from "react";

import { DEFAULT_SELLER_PERIOD, SELLER_PERIOD_OPTIONS } from "../constants";
import { formatMoney } from "../format";
import { useSalesBySeller } from "../hooks/useStats";
import type { SellerPeriod } from "../schemas";
import type { SalesBySellerStats } from "../types";
import { BarChart } from "./BarChart";
import { ChartCard } from "./ChartCard";
import { PeriodTabs } from "./PeriodTabs";

interface SellerChartProps {
  enabled: boolean;
}

/** Máximo de vendedores mostrados (igual que el `slice(0,15)` del SSR). */
const MAX_SELLERS = 15;

export function SellerChart({ enabled }: SellerChartProps) {
  const [period, setPeriod] = useState<SellerPeriod>(DEFAULT_SELLER_PERIOD);
  const query = useSalesBySeller(period, enabled);

  return (
    <ChartCard<SalesBySellerStats>
      title="Ventas por vendedor"
      action={
        <PeriodTabs
          value={period}
          options={SELLER_PERIOD_OPTIONS}
          onChange={setPeriod}
          ariaLabel="Período de ventas por vendedor"
        />
      }
      isPending={query.isPending}
      isError={query.isError}
      error={query.error}
      data={query.data}
    >
      {(stats) => (
        <BarChart
          barClassName="bg-indigo-500"
          // El backend ya entrega `data` ordenado por ingreso descendente.
          data={stats.data.slice(0, MAX_SELLERS).map((row) => ({
            label: row.seller,
            value: row.revenue,
            display: formatMoney(row.revenue),
          }))}
          emptyMessage="Sin ventas en el período."
        />
      )}
    </ChartCard>
  );
}
